import { type JSX, useState } from 'react';

import {
    Button,
    DialogActions,
    DialogContent,
    Divider,
    FormControlLabel,
    FormGroup,
    Switch,
    Typography
} from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { PopOver } from 'common/components/PopOver';
import { ColumnDefinition } from 'common/types/TypeDefs';

import {
    selectColumnVisibilityOverrides,
    selectIsColumnSettingsDialogOpen,
    setColumnSettingsDialogOpen
} from './columnSettingsSlice';
import { ColumnVisibilityModel, differsFromDefaults, resolveVisibilityModel } from './columnVisibilityStorage';
import { useCommitColumnVisibility } from './useCommitColumnVisibility';

const DIALOG_TITLE = 'Settings';
const RESET_BUTTON_TEST_ID = 'column-settings-reset-button-test-id';
const SAVE_BUTTON_TEST_ID = 'column-settings-save-button-test-id';
const LOCKED_SWITCH_CLASS = 'column-settings-switch-locked';

/*
    Every selector names switchBase to outrank MUI's own colours, written on that element.
    A locked switch keeps its thumb opaque and only fades the track: a translucent thumb
    would let the track edge show through it.
*/
const SWITCH_STYLES = {
    '& .MuiSwitch-switchBase .MuiSwitch-thumb': { backgroundColor: 'info.light' },
    '& .MuiSwitch-switchBase + .MuiSwitch-track': { backgroundColor: 'info.light', opacity: 0.5 },
    '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': { backgroundColor: 'primary.accent' },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'primary.accent', opacity: 0.5 },
    [`&.${LOCKED_SWITCH_CLASS} .MuiSwitch-switchBase`]: { cursor: 'default' },
    [`&.${LOCKED_SWITCH_CLASS} .MuiSwitch-switchBase:hover`]: { backgroundColor: 'transparent' },
    [`&.${LOCKED_SWITCH_CLASS} .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track`]: { opacity: 0.25 }
};

const visibleColumnsIn = (columnDefinitions: ColumnDefinition[], model: ColumnVisibilityModel) =>
    columnDefinitions.filter(({ columnId }) => model[columnId]);

type ColumnSettingsDialogProps = {
    gridKey: string;
    columnDefinitions: ColumnDefinition[];
};

export function ColumnSettingsDialog({
    gridKey,
    columnDefinitions
}: Readonly<ColumnSettingsDialogProps>): JSX.Element {
    const dispatch = useAppDispatch();
    const isDialogOpen = useAppSelector(selectIsColumnSettingsDialogOpen);
    const overrides = useAppSelector(selectColumnVisibilityOverrides(gridKey));
    const commitVisibility = useCommitColumnVisibility(gridKey, columnDefinitions);

    const closeDialog = () => dispatch(setColumnSettingsDialogOpen(false));

    const handleSave = (model: ColumnVisibilityModel) => {
        commitVisibility(model);
        closeDialog();
    };

    return (
        <PopOver
            title={DIALOG_TITLE}
            open={isDialogOpen}
            onClose={closeDialog}
        >
            {/* Unmounted with the dialog, so an abandoned draft never survives into the next visit. */}
            <ColumnVisibilityDraft
                columnDefinitions={columnDefinitions}
                initialModel={resolveVisibilityModel(columnDefinitions, overrides)}
                onSave={handleSave}
            />
        </PopOver>
    );
}

type ColumnVisibilityDraftProps = {
    columnDefinitions: ColumnDefinition[];
    initialModel: ColumnVisibilityModel;
    onSave: (model: ColumnVisibilityModel) => void;
};

function ColumnVisibilityDraft({
    columnDefinitions,
    initialModel,
    onSave
}: Readonly<ColumnVisibilityDraftProps>): JSX.Element {
    const [draftModel, setDraftModel] = useState(initialModel);
    const visibleColumns = visibleColumnsIn(columnDefinitions, draftModel);
    // A grid with no columns left is unusable, so the last visible one is locked on rather than disabled.
    const lockedColumn = visibleColumns.length === 1 ? visibleColumns[0] : undefined;

    const handleToggle = (columnId: string) => () => {
        setDraftModel(currentModel => {
            const isLastVisible = currentModel[columnId] && visibleColumnsIn(columnDefinitions, currentModel).length === 1;

            return isLastVisible ? currentModel : { ...currentModel, [columnId]: !currentModel[columnId] };
        });
    };

    const handleReset = () => {
        setDraftModel(resolveVisibilityModel(columnDefinitions, {}));
    };

    return (
        <>
            <DialogContent>
                <Typography color="info.light" sx={{ paddingBottom: '0.5rem' }}>
                    Choose which columns to display. Hidden columns are also left out of exports.
                </Typography>
                <Divider sx={{ borderColor: 'info.main' }} />
                <FormGroup sx={{ paddingTop: '0.5rem', alignItems: 'flex-start' }}>
                    {columnDefinitions.map(column => {
                        const isVisible = !!draftModel[column.columnId];
                        const isLocked = column === lockedColumn;

                        return (
                            <FormControlLabel
                                key={column.columnId}
                                label={column.columnName}
                                sx={{
                                    cursor: isLocked ? 'default' : undefined,
                                    '& .MuiFormControlLabel-label': { color: isVisible ? 'text.primary' : 'text.secondary' }
                                }}
                                control={
                                    <Switch
                                        sx={SWITCH_STYLES}
                                        className={isLocked ? LOCKED_SWITCH_CLASS : undefined}
                                        checked={isVisible}
                                        onChange={handleToggle(column.columnId)}
                                        disableRipple={isLocked}
                                        inputProps={{ 'aria-label': column.columnName, 'aria-disabled': isLocked }}
                                    />
                                }
                            />
                        );
                    })}
                </FormGroup>
            </DialogContent>
            <DialogActions sx={{ display: 'flex', justifyContent: 'center', padding: '1rem 0' }}>
                <Button
                    variant="outlined"
                    onClick={handleReset}
                    disabled={!differsFromDefaults(columnDefinitions, draftModel)}
                    data-testid={RESET_BUTTON_TEST_ID}
                >
                    Reset
                </Button>
                <Button
                    variant="contained"
                    onClick={() => onSave(draftModel)}
                    data-testid={SAVE_BUTTON_TEST_ID}
                >
                    Save
                </Button>
            </DialogActions>
        </>
    );
}
